import { useEffect, useState } from "react";
import { appCRUD } from "../../../actions/app";
import { char } from "../../../utils";
import {
  Button, Table, Tag, Card, Row, Col, Modal, Form, Input,
  Select, TimePicker, Space, Popconfirm, Empty, Alert
} from "antd";
import {
  PlusOutlined, EditOutlined, DeleteOutlined, UserOutlined,
  ClockCircleOutlined, SaveOutlined
} from "@ant-design/icons";
import { useNotification } from "../../../utils/notificationProvider";
import dayjs from "dayjs";

const { Option } = Select;
const { TextArea } = Input;

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

const PREFERENCE_TYPES = [
  { value: 'PREFERRED', label: 'Preferred', color: 'green', description: 'Instructor prefers this time slot' },
  { value: 'UNAVAILABLE', label: 'Unavailable', color: 'red', description: 'Instructor cannot teach at this time' },
];

export const InstructorPreferences = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [academics, setAcademics] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form] = Form.useForm();

  // Filters
  const [selectedAcademic, setSelectedAcademic] = useState(null);
  const [selectedSemester, setSelectedSemester] = useState(null);

  const openNotification = useNotification();

  useEffect(() => {
    loadAcademics();
  }, []);

  useEffect(() => {
    if (selectedAcademic) {
      loadSemesters(selectedAcademic);
    }
  }, [selectedAcademic]);

  useEffect(() => {
    if (selectedSemester) {
      loadPreferences();
    }
  }, [selectedSemester]);

  const loadAcademics = () => {
    appCRUD.loadAcademic(100, 0, "", "-id")
      .then((resp) => {
        const results = resp.data.results || resp.data || [];
        setAcademics(results);
        // Auto-select active academic year
        const active = results.find(a => a.is_active);
        if (active) {
          setSelectedAcademic(active.id);
        }
      })
      .catch(console.error);
  };

  const loadSemesters = (academicId) => {
    appCRUD.loadSemister(100, 0, academicId, "", "-id")
      .then((resp) => {
        const results = resp.data.results || resp.data || [];
        setSemesters(results);
        // Auto-select active semester
        const active = results.find(s => s.is_active);
        if (active) {
          setSelectedSemester(active.id);
        }
      })
      .catch(console.error);
  };

  const loadPreferences = () => {
    if (!selectedSemester) return;

    setLoading(true);
    appCRUD.loadInstructorPreferences(100, 0, null, selectedAcademic, selectedSemester)
      .then((resp) => {
        const results = resp.data.results || resp.data || [];
        setData(Array.isArray(results) ? results : []);
      })
      .catch((error) => {
        openNotification("error", "Error", error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleAdd = () => {
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({
      academic: selectedAcademic,
      semister: selectedSemester,
      preference_type: 'UNAVAILABLE',
    });
    setModalOpen(true);
  };

  const handleEdit = (record) => {
    setEditingId(record.id);
    form.setFieldsValue({
      instractor_name: record.instractor?.name || record.instractor_name,
      instractor_id: record.instractor?.instractor_id,
      academic: record.academic?.id,
      semister: record.semister?.id,
      day: record.day,
      start_time: record.start_time ? dayjs(record.start_time, 'HH:mm:ss') : null,
      end_time: record.end_time ? dayjs(record.end_time, 'HH:mm:ss') : null,
      preference_type: record.preference_type,
      reason: record.reason,
    });
    setModalOpen(true);
  };

  const handleDelete = (id) => {
    appCRUD.deleteInstructorPreference(id)
      .then(() => {
        openNotification("success", "Success", "Preference deleted");
        loadPreferences();
      })
      .catch((error) => {
        openNotification("error", "Error", error.message);
      });
  };

  const handleSave = () => {
    form.validateFields().then((values) => {
      const saveData = {
        instractor: values.instractor_id,
        academic: values.academic || selectedAcademic,
        semister: values.semister || selectedSemester,
        day: values.day,
        start_time: values.start_time?.format('HH:mm:ss'),
        end_time: values.end_time?.format('HH:mm:ss'),
        preference_type: values.preference_type,
        reason: values.reason,
      };

      const promise = editingId
        ? appCRUD.updateInstructorPreference(editingId, saveData)
        : appCRUD.addInstructorPreference(saveData);

      promise
        .then(() => {
          openNotification("success", "Success", editingId ? "Preference updated" : "Preference created");
          setModalOpen(false);
          loadPreferences();
        })
        .catch((error) => {
          openNotification("error", "Error", error.response?.data?.detail || error.message);
        });
    });
  };

  const columns = [
    {
      title: "#",
      key: "sn",
      width: 50,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Instructor",
      key: "instractor",
      render: (_, record) => (
        <span>
          <UserOutlined className="me-2" />
          {char.toTitleCase(record.instractor?.name || "Unknown")}
        </span>
      ),
    },
    {
      title: "Day",
      dataIndex: "day",
      key: "day",
      width: 120,
      render: (day) => <Tag color="blue">{char.toTitleCase(day)}</Tag>,
      filters: DAYS.map(d => ({ text: d, value: d.toLowerCase() })),
      onFilter: (value, record) => record.day?.toLowerCase() === value,
    },
    {
      title: "Time",
      key: "time",
      width: 150,
      render: (_, record) => (
        <span>
          <ClockCircleOutlined className="me-2" />
          {record.start_time?.slice(0, 5)} - {record.end_time?.slice(0, 5)}
        </span>
      ),
    },
    {
      title: "Type",
      dataIndex: "preference_type",
      key: "preference_type",
      width: 130,
      render: (type) => {
        const config = PREFERENCE_TYPES.find(p => p.value === type);
        return <Tag color={config?.color}>{config?.label || type}</Tag>;
      },
      filters: PREFERENCE_TYPES.map(t => ({ text: t.label, value: t.value })),
      onFilter: (value, record) => record.preference_type === value,
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      ellipsis: true,
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Delete this preference?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Group by instructor
  const instructorSummary = {};
  data.forEach(pref => {
    const name = pref.instractor?.name || "Unknown";
    if (!instructorSummary[name]) {
      instructorSummary[name] = { preferred: 0, unavailable: 0 };
    }
    if (pref.preference_type === 'PREFERRED') {
      instructorSummary[name].preferred++;
    } else {
      instructorSummary[name].unavailable++;
    }
  });

  return (
    <div className="container-fluid">
      <div className="row mb-3">
        <div className="col-12">
          <h5 className="fw-bold text-dark mb-0">
            <UserOutlined className="me-2" />
            Instructor Preferences
          </h5>
          <p className="text-muted small mb-0">
            Configure instructor availability and preferred teaching times
          </p>
        </div>
        <hr className="mt-3" />
      </div>

      {/* Info Alert */}
      <Alert
        message="How Preferences Work"
        description={
          <ul className="mb-0 ps-3">
            <li><Tag color="red">UNAVAILABLE</Tag> - Instructor cannot be scheduled during this time</li>
            <li><Tag color="green">PREFERRED</Tag> - Scheduler will try to assign this time slot first (bonus scoring)</li>
          </ul>
        }
        type="info"
        showIcon
        className="mb-3"
      />

      {/* Filters */}
      <Card size="small" className="mb-3">
        <Row gutter={16} align="middle">
          <Col xs={24} sm={8}>
            <Select
              placeholder="Select Academic Year"
              style={{ width: "100%" }}
              value={selectedAcademic}
              onChange={setSelectedAcademic}
            >
              {academics.map((a) => (
                <Option key={a.id} value={a.id}>
                  {a.name} {a.is_active && <Tag color="green" size="small">Active</Tag>}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={8}>
            <Select
              placeholder="Select Semester"
              style={{ width: "100%" }}
              value={selectedSemester}
              onChange={setSelectedSemester}
              disabled={!selectedAcademic}
            >
              {semesters.map((s) => (
                <Option key={s.id} value={s.id}>
                  {char.toTitleCase(s.name)} {s.is_active && <Tag color="green" size="small">Active</Tag>}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={8}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              disabled={!selectedSemester}
            >
              Add Preference
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Main Content */}
      {!selectedSemester ? (
        <Card>
          <Empty description="Select an academic year and semester to view preferences" />
        </Card>
      ) : (
        <Card>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <span className="text-muted">
              {data.length} preference{data.length !== 1 ? 's' : ''} configured
            </span>
          </div>

          <Table
            loading={loading}
            columns={columns}
            dataSource={data}
            rowKey="id"
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} preferences`,
            }}
            size="small"
            bordered
          />
        </Card>
      )}

      {/* Summary by Instructor */}
      {data.length > 0 && (
        <Card title="Summary by Instructor" className="mt-3" size="small">
          <Row gutter={16}>
            {Object.entries(instructorSummary).map(([name, counts]) => (
              <Col key={name} xs={12} sm={8} md={6} className="mb-2">
                <Card size="small" type="inner">
                  <div className="text-center">
                    <UserOutlined className="fs-4 text-primary" />
                    <div className="fw-bold mt-1">{char.toTitleCase(name)}</div>
                    <div className="small">
                      {counts.unavailable > 0 && (
                        <Tag color="red">{counts.unavailable} Unavailable</Tag>
                      )}
                      {counts.preferred > 0 && (
                        <Tag color="green">{counts.preferred} Preferred</Tag>
                      )}
                    </div>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </Card>
      )}

      {/* Add/Edit Modal */}
      <Modal
        title={editingId ? "Edit Instructor Preference" : "Add Instructor Preference"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setModalOpen(false)}>
            Cancel
          </Button>,
          <Button key="save" type="primary" icon={<SaveOutlined />} onClick={handleSave}>
            Save
          </Button>,
        ]}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="instractor_name"
                label="Instructor Name"
                rules={[{ required: true, message: "Please enter instructor name" }]}
              >
                <Input placeholder="Enter instructor name" prefix={<UserOutlined />} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="instractor_id"
                label="Instructor ID (from SOAS)"
                rules={[{ required: true, message: "Please enter instructor ID" }]}
                extra="This should match the instructor ID from the course allocation"
              >
                <Input placeholder="e.g., 12345" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="day"
                label="Day"
                rules={[{ required: true, message: "Please select a day" }]}
              >
                <Select placeholder="Select day">
                  {DAYS.map((d) => (
                    <Option key={d} value={d.toLowerCase()}>{d}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="preference_type"
                label="Preference Type"
                rules={[{ required: true, message: "Please select type" }]}
              >
                <Select placeholder="Select type">
                  {PREFERENCE_TYPES.map((type) => (
                    <Option key={type.value} value={type.value}>
                      <Tag color={type.color}>{type.label}</Tag>
                      <span className="small text-muted ms-2">{type.description}</span>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="start_time"
                label="Start Time"
                rules={[{ required: true, message: "Please select start time" }]}
              >
                <TimePicker format="HH:mm" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="end_time"
                label="End Time"
                rules={[{ required: true, message: "Please select end time" }]}
              >
                <TimePicker format="HH:mm" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="reason" label="Reason (Optional)">
            <TextArea rows={2} placeholder="Why is this time preferred/unavailable?" />
          </Form.Item>

          {/* Hidden fields for academic and semester */}
          <Form.Item name="academic" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="semister" hidden>
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default InstructorPreferences;
